import { useState, useEffect, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { api, API } from "@/lib/api";
import { ArrowLeft, Images, Loader2, ZoomIn, Calendar, Tag as TagIcon, Share2, Check } from "lucide-react";
import { Lightbox } from "@/components/Lightbox";
import { Footer } from "@/components/landing/FooterCTA";
import { Navbar } from "@/components/landing/Navbar";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import logo from "../assets/logo.png";

const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
function fmtDate(d) {
  if (!d) return null;
  const [y, m, day] = d.slice(0, 10).split("-");
  if (!y) return null;
  if (day && m) return `${parseInt(day, 10)} de ${MONTHS[parseInt(m, 10) - 1]}. ${y}`;
  return y;
}

export default function AlbumPage() {
  const { slug } = useParams();
  const [album, setAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [copied, setCopied] = useState(false);

  const share = async () => {
    const url = `${API}/og/galerias/${slug}`;
    const title = album ? `Álbum: ${album.name}` : "Galería del club";
    if (navigator.share) {
      try { await navigator.share({ title, text: title, url }); return; } catch { /* cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { window.prompt("Copiá el enlace del álbum:", url); }
  };

  useEffect(() => {
    setLoading(true);
    api.get(`/gallery/albums/by-slug/${slug}`)
      .then(({ data }) => setAlbum(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const photos = album?.photos || [];
  const nav = useCallback((dir) => {
    setLightbox((i) => (i === null ? i : (i + dir + photos.length) % photos.length));
  }, [photos.length]);

  const d = album ? (album.event_date || (album.created_at ? album.created_at.slice(0, 10) : null)) : null;

  return (
    <div className="min-h-screen bg-[#eef1f7] flex flex-col">
      <Navbar />
      <div className="pt-16 sm:pt-20">
        <Breadcrumbs items={[
          { label: "Galería", to: "/galerias" },
          { label: album ? album.name : "Álbum" },
        ]} />
      </div>

      {loading ? (
        <div className="flex-1 py-24 flex justify-center text-navy/40"><Loader2 className="h-7 w-7 animate-spin" /></div>
      ) : notFound || !album ? (
        <div className="flex-1 py-24 text-center text-gray-400">
          <Images className="h-10 w-10 mx-auto" />
          <p className="mt-3">No encontramos este álbum.</p>
          <Link to="/galerias" className="mt-4 inline-block text-sm font-semibold text-navy underline">Ver todos los álbumes</Link>
        </div>
      ) : (
        <>
          <section className="bg-navy text-white">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-10 pt-4">
              <span className="section-tag section-tag--dark">{album.category}</span>
              <h1 data-testid="album-title" className="mt-4 text-3xl sm:text-5xl font-extrabold tracking-tight text-white">{album.name}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/70">
                {d && <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4 text-gold" /> {fmtDate(d)}</span>}
                <span className="inline-flex items-center gap-1.5"><Images className="h-4 w-4 text-gold" /> {photos.length} foto{photos.length === 1 ? "" : "s"}</span>
              </div>
              {album.description && <p className="mt-4 text-base sm:text-lg text-white/80 max-w-2xl">{album.description}</p>}
              {album.tags?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {album.tags.map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 text-xs font-semibold text-navy bg-gold/90 px-2.5 py-1 rounded-full">
                      <TagIcon className="h-3 w-3" /> {t}
                    </span>
                  ))}
                </div>
              )}
              <button
                onClick={share}
                data-testid="album-share"
                className="mt-5 inline-flex items-center gap-2 bg-white/10 hover:bg-gold hover:text-navy border border-white/20 text-white font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors"
              >
                {copied ? <><Check className="h-4 w-4" /> Enlace copiado</> : <><Share2 className="h-4 w-4" /> Compartir álbum</>}
              </button>
            </div>
          </section>

          <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-8">
            {photos.length === 0 ? (
              <p className="py-16 text-center text-sm text-gray-400">Este álbum todavía no tiene fotos.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                {photos.map((p, i) => (
                  <button
                    key={p.id}
                    data-testid={`album-photo-${i}`}
                    onClick={() => setLightbox(i)}
                    className="group relative aspect-square rounded-xl overflow-hidden border border-gray-100 cursor-zoom-in bg-white"
                  >
                    <img src={p.url} alt={p.alt} loading="lazy" decoding="async"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <span className="absolute inset-0 bg-navy/0 group-hover:bg-navy/25 transition-colors flex items-center justify-center">
                      <ZoomIn className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </span>
                  </button>
                ))}
              </div>
            )}
          </main>
        </>
      )}

      <AnimatePresence>
        {lightbox !== null && photos.length > 0 && (
          <Lightbox items={photos} index={lightbox} onClose={() => setLightbox(null)} onNav={nav} />
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
